import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  Shield,
  Plus,
  Edit,
  Trash2,
  GripVertical,
  ExternalLink,
  Image as ImageIcon,
  Check,
  X,
  FolderOpen
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import AdminMediaLibrary from './AdminMediaLibrary';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    url: '',
    active: true
  });
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  
  const { toast } = useToast();
  const { token } = useAuth();

  // Handle media selection from library
  const handleMediaSelect = (media) => {
    const imageUrl = toRelativeMediaPath(media.versions?.medium || media.url || `/api/media/${media.filename}`);
    setFormData({ ...formData, logo: imageUrl });
    setShowMediaPicker(false);
    toast({
      title: 'Bilde valgt',
      description: `${media.title || media.filename} er valgt som logo`
    });
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/partners?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste partnere",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (partner = null) => {
    if (partner) {
      setEditingPartner(partner);
      setFormData({
        name: partner.name,
        description: partner.description,
        logo: partner.logo,
        url: partner.url,
        active: partner.active
      });
    } else {
      setEditingPartner(null);
      setFormData({
        name: '',
        description: '',
        logo: '',
        url: '',
        active: true
      });
    }
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast({
        title: "Feil",
        description: "Navn er påkrevd",
        variant: "destructive"
      });
      return;
    }

    try {
      setSaving(true);
      
      if (editingPartner) {
        await axios.put(`${API}/partners/${editingPartner.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Oppdatert! ✅",
          description: `${formData.name} er oppdatert`
        });
      } else {
        await axios.post(`${API}/partners`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast({
          title: "Opprettet! ✅",
          description: `${formData.name} er lagt til`
        });
      }
      
      setIsModalOpen(false);
      await fetchPartners();
    } catch (error) {
      console.error('Error saving partner:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke lagre partner",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (partnerId, partnerName) => {
    if (!window.confirm(`Er du sikker på at du vil slette "${partnerName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/partners/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: `${partnerName} er fjernet`
      });
      
      await fetchPartners();
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette partner",
        variant: "destructive"
      });
    }
  };

  const handleToggleActive = async (partner) => {
    try {
      await axios.put(`${API}/partners/${partner.id}`, {
        active: !partner.active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: partner.active ? "Deaktivert" : "Aktivert",
        description: `${partner.name} er nå ${partner.active ? 'skjult' : 'synlig'}`
      });
      
      await fetchPartners();
    } catch (error) {
      console.error('Error toggling partner:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster partnere...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-yellow-400 to-red-500 bg-clip-text text-transparent">
              Partnere & Sertifiseringer
            </span>
          </h2>
          <p className="text-gray-400">Administrer logoer som vises i Trust Bar på startsiden og i footer</p>
        </div>
        <Button
          onClick={() => handleOpenModal()}
          className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Legg til partner
        </Button>
      </div>

      {/* Partners List */}
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-yellow-400" />
            Aktive partnere ({partners.filter(p => p.active).length})
          </CardTitle>
          <CardDescription className="text-gray-400">
            Dra for å endre rekkefølge. Klikk på en partner for å redigere.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {partners.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg">Ingen partnere ennå</p>
              <p className="text-sm mt-2">Klikk "Legg til partner" for å komme i gang</p>
            </div>
          ) : (
            <div className="space-y-2">
              {partners.map((partner) => (
                <div
                  key={partner.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                    partner.active 
                      ? 'bg-gray-900/50 border-gray-700 hover:border-gray-600' 
                      : 'bg-gray-900/30 border-gray-800 opacity-60'
                  }`}
                >
                  {/* Drag Handle */}
                  <GripVertical className="w-5 h-5 text-gray-600 cursor-grab" />
                  
                  {/* Logo Preview */}
                  <div className="w-16 h-12 bg-gray-800 rounded flex items-center justify-center overflow-hidden">
                    {partner.logo && typeof partner.logo === 'string' && partner.logo.length > 0 ? (
                      <img
                        src={partner.logo.startsWith('http') ? partner.logo : `${BACKEND_URL}${partner.logo}`}
                        alt={partner.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <ImageIcon className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{partner.name}</h3>
                    <p className="text-gray-500 text-sm truncate">{partner.description}</p>
                  </div>
                  
                  {/* Status Badge */}
                  <Badge 
                    className={partner.active 
                      ? "bg-green-600/20 text-green-400 border-green-600/50" 
                      : "bg-gray-600/20 text-gray-400 border-gray-600/50"
                    }
                  >
                    {partner.active ? 'Aktiv' : 'Skjult'}
                  </Badge>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {partner.url && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-600 text-gray-300"
                        onClick={() => window.open(partner.url, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className={partner.active 
                        ? "border-yellow-600 text-yellow-400" 
                        : "border-green-600 text-green-400"
                      }
                      onClick={() => handleToggleActive(partner)}
                    >
                      {partner.active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-blue-600 text-blue-400"
                      onClick={() => handleOpenModal(partner)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-600 text-red-400 hover:bg-red-600/20"
                      onClick={() => handleDelete(partner.id, partner.name)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPartner ? 'Rediger partner' : 'Legg til partner'}</DialogTitle>
            <DialogDescription className="text-gray-400">
              Informasjonen vises i Trust Bar på startsiden og i footer
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Navn *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="F.eks. Bosch Car Service"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Beskrivelse (alt-tekst for SEO)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="F.eks. Offisiell Bosch Car Service partner - kvalitetssikret service"
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
              />
              <p className="text-xs text-gray-500 mt-1">Brukes som alt-tekst for logoen (viktig for SEO/tilgjengelighet)</p>
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Logo</Label>
              <div className="flex gap-2">
                <Input
                  value={formData.logo}
                  onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                  placeholder="Lim inn URL eller velg fra mediebibliotek"
                  className="flex-1 bg-gray-800 border-gray-700 text-white"
                  maxLength={500}
                />
                <Button
                  type="button"
                  onClick={() => setShowMediaPicker(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Velg bilde
                </Button>
              </div>
              {formData.logo && typeof formData.logo === 'string' && formData.logo.length > 0 && (
                <div className="flex items-center gap-3 mt-2 p-2 bg-gray-800/50 rounded-lg">
                  <div className="w-16 h-12 bg-gray-900 rounded flex items-center justify-center overflow-hidden">
                    <img 
                      src={formData.logo.startsWith('http') ? formData.logo : `${BACKEND_URL}${formData.logo}`} 
                      alt="Preview" 
                      className="max-w-full max-h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm text-gray-300">Forhåndsvisning</span>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, logo: '' })}
                      className="block text-xs text-red-400 hover:text-red-300 mt-1"
                    >
                      Fjern bilde
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-gray-300 mb-2 block">Nettside URL</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://www.partner.no"
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 rounded border-gray-700 bg-gray-900"
              />
              <Label htmlFor="active" className="text-gray-300 cursor-pointer">
                Aktiv (synlig på nettsiden)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              className="border-gray-600 text-gray-300"
            >
              Avbryt
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-gradient-to-r from-red-600 to-yellow-500"
            >
              {saving ? 'Lagrer...' : editingPartner ? 'Oppdater' : 'Legg til'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <span>Velg logo fra mediebibliotek</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Klikk på et bilde for å velge det som logo
            </DialogDescription>
          </DialogHeader>
          
          <AdminMediaLibrary 
            pickerMode={true} 
            onSelect={handleMediaSelect}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPartners;
